import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PROJECT_TYPES = [
  "Rénovation complète",
  "Installation cuisine",
  "Salle de bain",
  "Peinture",
  "Enduit",
  "Carrelage",
  "Autre",
];

export default function DevisPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    city: "",
    project_type: "Rénovation complète",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAFAFA] text-[#1A1918] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-36 pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#C86641]">◆ Demande de devis</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-6">
            Décrivez votre projet,<br />
            <span className="italic font-light">je reviens vers vous </span>
            <span className="text-[#C86641]">rapidement.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#5C5A56] text-lg">
            Remplissez le formulaire ci-dessous. Plus vous donnez de détails, plus mon estimation sera précise.
            Réponse sous 48h ouvrées.
          </p>

          {success ? (
            <div data-testid="devis-success" className="mt-12 border border-[#E5E3DD] bg-[#F3F1ED] p-10 text-center">
              <CheckCircle2 size={48} className="text-[#C86641] mx-auto" />
              <h2 className="font-display text-3xl mt-6 tracking-tight">Demande envoyée !</h2>
              <p className="mt-4 text-[#5C5A56] max-w-md mx-auto">
                Merci pour votre demande. Je vais l'étudier et vous recontacter sous 48h ouvrées
                au numéro indiqué ou par email.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/" className="bg-[#1A1918] text-[#FAFAFA] px-6 py-3 font-semibold hover:bg-[#C86641] transition-colors" data-testid="devis-back-home">
                  Retour à l'accueil
                </Link>
                {!user && (
                  <Link to="/inscription" className="border border-[#1A1918] px-6 py-3 font-semibold hover:bg-[#1A1918] hover:text-[#FAFAFA] transition-colors" data-testid="devis-create-account">
                    Créer un compte pour suivre ma demande
                  </Link>
                )}
                {user?.role === "client" && (
                  <button onClick={() => navigate("/espace-client")} className="border border-[#1A1918] px-6 py-3 font-semibold hover:bg-[#1A1918] hover:text-[#FAFAFA] transition-colors" data-testid="devis-go-client-area">
                    Aller à mon espace
                  </button>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="devis-form">
              <Field label="Nom complet" testId="devis-fullname">
                <input
                  required
                  value={form.full_name}
                  onChange={update("full_name")}
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                  data-testid="devis-fullname-input"
                />
              </Field>
              <Field label="Email" testId="devis-email">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                  data-testid="devis-email-input"
                />
              </Field>
              <Field label="Téléphone" testId="devis-phone">
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                  data-testid="devis-phone-input"
                />
              </Field>
              <Field label="Ville" testId="devis-city">
                <input
                  value={form.city}
                  onChange={update("city")}
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                  data-testid="devis-city-input"
                />
              </Field>
              <Field label="Type de projet" testId="devis-project-type" full>
                <select
                  value={form.project_type}
                  onChange={update("project_type")}
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                  data-testid="devis-project-type-input"
                >
                  {PROJECT_TYPES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Description du projet" testId="devis-description" full>
                <textarea
                  required
                  rows={6}
                  value={form.description}
                  onChange={update("description")}
                  placeholder="Surface, pièces concernées, matériaux souhaités, délai..."
                  className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors resize-none"
                  data-testid="devis-description-input"
                />
              </Field>

              {error && (
                <div data-testid="devis-error" className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <div className="md:col-span-2 flex items-center justify-between mt-4">
                <div className="text-sm text-[#5C5A56]">Vos informations restent confidentielles.</div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="devis-submit-button"
                  className="bg-[#C86641] text-[#FAFAFA] px-8 py-4 font-semibold hover:bg-[#A65132] transition-colors inline-flex items-center gap-3 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  Envoyer ma demande
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children, testId, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`} data-testid={`${testId}-field`}>
      <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">{label}</div>
      {children}
    </label>
  );
}
